import React, { useState } from "react";
import { ChefHat, Sparkles, Instagram, Quote } from "lucide-react";

/* ============================================
   CRETAN MAP — interactive (CSS-positioned pins)
   ============================================ */

const MAP_PINS = [
  // Coordinates calibrated to the AI-rendered Crete map (crete-map.png)
  { id: "chania",   x: 14, y: 38, label: "Χανιά / Κολυμπάρι", desc: "Ιστορικός ελαιώνας · κρητική παράδοση 4 γενεών — πρώτη ψυχρή έκθλιψη EVOO." },
  { id: "rethymno", x: 33, y: 42, label: "Ρέθυμνο · Ψηλορείτι", desc: "Άγριο θυμαρίσιο μέλι από μελισσοκόμους του Ψηλορείτη." },
  { id: "heraklion",x: 52, y: 50, label: "Ηράκλειο", desc: "Πέτρινα ελαιοτριβεία (εlaiotriveia) · καρδιά της παραγωγής premium ελαιολάδου." },
  { id: "archanes", x: 56, y: 60, label: "Αρχάνες", desc: "Boutique οινοποιεία · κρητικές ποικιλίες Βιδιανό & Λιάτικο." },
  { id: "lasithi",  x: 80, y: 50, label: "Λασίθι · Σητεία", desc: "Παραδοσιακή τσικουδιά (ρακή) σε χάλκινα καζάνια — αιώνες παράδοσης." },
];

export const CretanMap = () => {
  const [active, setActive] = useState("heraklion");
  const current = MAP_PINS.find((p) => p.id === active) || MAP_PINS[0];

  return (
    <section id="map" data-testid="cretan-map-section" className="relative py-24 sm:py-32 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <p className="font-display text-[10px] tracking-[0.55em] uppercase text-[color:var(--goldenelia-gold)] mb-4">
            Από πού έρχονται
          </p>
          <h2 className="font-serif-luxury text-4xl sm:text-5xl gold-gradient-text mb-4">
            Ο χάρτης της Κρήτης
          </h2>
          <div className="gold-divider max-w-xs mx-auto"><span className="diamond" /></div>
        </div>

        <div className="grid md:grid-cols-[1.4fr_1fr] gap-10 items-center">
          {/* Real Crete map illustration (AI-rendered gold topo on transparent PNG) */}
          <div className="relative aspect-[16/7] bg-black/40 border border-[color:var(--goldenelia-gold)]/25 overflow-hidden">
            <img
              src="/crete-map.png"
              alt="Χάρτης της Κρήτης"
              className="absolute inset-0 w-full h-full object-contain p-4"
              style={{ filter: "drop-shadow(0 0 18px rgba(201,169,97,0.15))" }}
              draggable={false}
            />
            {MAP_PINS.map((p) => (
              <button
                key={p.id}
                onClick={() => setActive(p.id)}
                data-testid={`map-pin-${p.id}`}
                className="absolute -translate-x-1/2 -translate-y-1/2 group focus:outline-none"
                style={{ left: `${p.x}%`, top: `${p.y}%` }}
                aria-label={p.label}
              >
                <span
                  className={`block w-3.5 h-3.5 rounded-full transition-all duration-300 ${active === p.id ? "scale-150 bg-[color:var(--goldenelia-gold-bright)]" : "bg-[color:var(--goldenelia-gold)] group-hover:scale-125"}`}
                  style={{ boxShadow: "0 0 22px rgba(230,201,138,0.85), 0 0 6px rgba(230,201,138,1)" }}
                />
                {active === p.id && (
                  <span
                    aria-hidden="true"
                    className="absolute inset-0 m-auto block w-7 h-7 rounded-full border border-[color:var(--goldenelia-gold)]/60 -translate-x-1/2 -translate-y-1/2"
                    style={{ left: "50%", top: "50%", animation: "logoPulse 2s ease-in-out infinite" }}
                  />
                )}
                {/* Hover tooltip */}
                <span className="absolute left-1/2 -translate-x-1/2 top-full mt-2 whitespace-nowrap px-2 py-1 bg-black/80 border border-[color:var(--goldenelia-gold)]/40 font-display text-[9px] tracking-[0.2em] uppercase text-[color:var(--goldenelia-gold)] opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity">
                  {p.label}
                </span>
              </button>
            ))}
          </div>

          {/* Detail panel */}
          <div className="p-6 border border-[color:var(--goldenelia-gold)]/25 bg-black/50 min-h-[200px]">
            <p className="font-display text-[10px] tracking-[0.4em] uppercase text-[color:var(--goldenelia-gold)] mb-2">
              Περιοχή
            </p>
            <h3 className="font-serif-luxury text-3xl gold-gradient-text mb-4">{current.label}</h3>
            <p className="font-body text-[15px] text-[color:var(--goldenelia-cream)]/80 leading-relaxed">{current.desc}</p>
            <p className="font-display text-[9px] tracking-[0.35em] uppercase text-[color:var(--goldenelia-cream)]/40 mt-6">
              {MAP_PINS.length} τοποθεσίες · κάντε κλικ στα σημεία
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================
   PRODUCERS
   ============================================ */

const PRODUCERS = [
  {
    name: "Οικογένεια Σταυρακάκη",
    role: "Παραγωγοί Ελαιολάδου",
    region: "Κολυμπάρι · 4η γενιά",
    quote: "Το λάδι μας είναι η ψυχή της γης μας — η Κρήτη σε κάθε σταγόνα.",
    img: "/gallery/oil.jpg",
  },
  {
    name: "Νικόλαος & Ελένη",
    role: "Μελισσοκόμοι",
    region: "Ψηλορείτι · 30+ χρόνια",
    quote: "Τα μελίσσια μας ταξιδεύουν στα θυμαρίσια βουνά. Μέλι αληθινό, χωρίς εκπτώσεις.",
    img: "/gallery/honey.jpg",
  },
  {
    name: "Κτήμα Δαμηγού",
    role: "Οινοποιοί",
    region: "Αρχάνες · 1992",
    quote: "Κρητικές ποικιλίες που χάθηκαν, ζωντανές πάλι μέσα από τα κρασιά μας.",
    img: "/gallery/wine.jpg",
  },
];

export const Producers = () => (
  <section id="producers" data-testid="producers-section" className="relative py-24 sm:py-32 px-6">
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-14">
        <p className="font-display text-[10px] tracking-[0.55em] uppercase text-[color:var(--goldenelia-gold)] mb-4">
          Οι Άνθρωποι Πίσω
        </p>
        <h2 className="font-serif-luxury text-4xl sm:text-5xl gold-gradient-text mb-4">
          Οι παραγωγοί μας
        </h2>
        <div className="gold-divider max-w-xs mx-auto"><span className="diamond" /></div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {PRODUCERS.map((p, i) => (
          <article
            key={p.name}
            data-testid={`producer-${i}`}
            className="group relative bg-black border border-[color:var(--goldenelia-gold)]/20 hover:border-[color:var(--goldenelia-gold)]/55 transition-colors overflow-hidden"
          >
            <div className="aspect-[4/3] overflow-hidden">
              <img src={p.img} alt={p.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-110" />
            </div>
            <div className="p-6">
              <p className="font-display text-[9px] tracking-[0.4em] uppercase text-[color:var(--goldenelia-gold)] mb-1">
                {p.role}
              </p>
              <h3 className="font-serif-luxury text-2xl text-[color:var(--goldenelia-cream)] mb-1">{p.name}</h3>
              <p className="font-body text-xs text-[color:var(--goldenelia-cream)]/55 mb-4">{p.region}</p>
              <p className="font-serif-luxury italic text-[15px] text-[color:var(--goldenelia-cream)]/80 leading-snug">
                <Quote size={14} className="inline -translate-y-1 mr-1 text-[color:var(--goldenelia-gold)]/60" />
                {p.quote}
              </p>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

/* ============================================
   RECIPES BLOG
   ============================================ */

const RECIPES = [
  {
    title: "Κρητική Σαλάτα με EVOO Reserve",
    subtitle: "Με ντομάτα, παξιμάδι & ξινομυζήθρα",
    time: "10'",
    serves: "4",
    img: "/cats/olives.jpg",
    body: [
      "1 ώριμη ντομάτα κομμένη σε κύβους",
      "1 παξιμάδι ολικής αλέσεως — βρεγμένο για 30''",
      "100g ξινομυζήθρα (ή φέτα)",
      "Αλάτι, ρίγανη Κρήτης, 4 κουτ. EVOO Reserve",
      "Σερβίρετε αμέσως με το λάδι μας — η γεύση μιλάει μόνη της.",
    ],
  },
  {
    title: "Γιαούρτι με Θυμαρίσιο Μέλι",
    subtitle: "Πρωινό για όλη την οικογένεια",
    time: "5'",
    serves: "1",
    img: "/cats/honey.jpg",
    body: [
      "200g στραγγιστό γιαούρτι Κρητικό",
      "2 κουτ. θυμαρίσιο μέλι Golden Elia",
      "Καρύδια, αμύγδαλα ή φρέσκα φρούτα",
      "Λίγο κανέλα και ξύσμα λεμονιού.",
    ],
  },
  {
    title: "Ραγού με Κόκκινο Κρασί",
    subtitle: "Αργομαγειρεμένο μοσχάρι Λιάτικο",
    time: "3 ώρες",
    serves: "6",
    img: "/cats/wine.jpg",
    body: [
      "1.5kg μοσχαρίσιο σπάλα σε κύβους",
      "1 φιάλη Λιάτικο κρασί Golden Elia",
      "Κρεμμύδια, σκόρδο, καρότα, σέλινο",
      "Δάφνη, μπαχάρια, αλάτι. Σιγή σε γάστρα για 2.5h.",
    ],
  },
];

export const Recipes = () => {
  const [open, setOpen] = useState(null);
  return (
    <section id="recipes" data-testid="recipes-section" className="relative py-24 sm:py-32 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <p className="font-display text-[10px] tracking-[0.55em] uppercase text-[color:var(--goldenelia-gold)] mb-4">
            <ChefHat size={12} className="inline -translate-y-0.5 mr-1" /> Συνταγές
          </p>
          <h2 className="font-serif-luxury text-4xl sm:text-5xl gold-gradient-text mb-4">
            Από την κουζίνα μας
          </h2>
          <div className="gold-divider max-w-xs mx-auto"><span className="diamond" /></div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {RECIPES.map((r, i) => (
            <button
              key={r.title}
              data-testid={`recipe-${i}`}
              onClick={() => setOpen(i)}
              className="group relative text-left bg-black border border-[color:var(--goldenelia-gold)]/20 hover:border-[color:var(--goldenelia-gold)]/55 transition-colors overflow-hidden"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img src={r.img} alt={r.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-110" />
              </div>
              <div className="p-5">
                <p className="font-display text-[9px] tracking-[0.4em] uppercase text-[color:var(--goldenelia-gold)] mb-1">
                  {r.time} · {r.serves} άτομα
                </p>
                <h3 className="font-serif-luxury text-xl text-[color:var(--goldenelia-cream)] mb-1">{r.title}</h3>
                <p className="font-body italic text-xs text-[color:var(--goldenelia-cream)]/55">{r.subtitle}</p>
              </div>
            </button>
          ))}
        </div>

        {open !== null && (
          <div
            data-testid="recipe-modal"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/85 backdrop-blur"
            onClick={() => setOpen(null)}
          >
            <div
              className="max-w-2xl w-full bg-black border border-[color:var(--goldenelia-gold)]/40 p-8 max-h-[85vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <p className="font-display text-[10px] tracking-[0.4em] uppercase text-[color:var(--goldenelia-gold)] mb-2">
                {RECIPES[open].time} · {RECIPES[open].serves} άτομα
              </p>
              <h3 className="font-serif-luxury text-3xl gold-gradient-text mb-2">{RECIPES[open].title}</h3>
              <p className="font-serif-luxury italic text-[color:var(--goldenelia-cream)]/75 mb-6">{RECIPES[open].subtitle}</p>
              <ul className="space-y-2 font-body text-[color:var(--goldenelia-cream)]/85">
                {RECIPES[open].body.map((line, j) => (
                  <li key={j} className="flex gap-3"><span className="text-[color:var(--goldenelia-gold)]">·</span>{line}</li>
                ))}
              </ul>
              <button
                onClick={() => setOpen(null)}
                className="mt-8 btn-ghost-gold !px-6 !py-2 text-[11px]"
              >
                Κλείσιμο
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

/* ============================================
   TESTIMONIALS
   ============================================ */

const REVIEWS = [
  { name: "Maria K.", role: "Wedding Planner · Mykonos", text: "Έχω παραγγείλει 5 φορές για γάμους. Πάντα impeccable — κάθε λεπτομέρεια." , stars: 5 },
  { name: "Hotel Daios Cove", role: "5★ Luxury Resort · Crete", text: "The Golden Elia welcome boxes elevate every guest experience.", stars: 5 },
  { name: "Δημήτρης Π.", role: "Corporate Buyer · Athens", text: "150 boxes για τα στελέχη μας — feedback εκπληκτικό. Συστήνω!", stars: 5 },
  { name: "Sofia M.", role: "Bride", text: "Τα προϊόντα τόσο όμορφα, που η συσκευασία ήταν δώρο από μόνη της.", stars: 5 },
];

export const Testimonials = () => (
  <section id="testimonials" data-testid="testimonials-section" className="relative py-24 sm:py-32 px-6">
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-14">
        <p className="font-display text-[10px] tracking-[0.55em] uppercase text-[color:var(--goldenelia-gold)] mb-4">
          Μαρτυρίες
        </p>
        <h2 className="font-serif-luxury text-4xl sm:text-5xl gold-gradient-text mb-4">
          Λένε για εμάς
        </h2>
        <div className="gold-divider max-w-xs mx-auto"><span className="diamond" /></div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {REVIEWS.map((r, i) => (
          <article
            key={i}
            data-testid={`testimonial-${i}`}
            className="p-6 border border-[color:var(--goldenelia-gold)]/20 bg-black/40 hover:border-[color:var(--goldenelia-gold)]/50 transition-colors"
          >
            <div className="flex gap-0.5 text-[color:var(--goldenelia-gold)] mb-4">
              {Array.from({ length: r.stars }).map((_, j) => (
                <Sparkles key={j} size={12} fill="currentColor" />
              ))}
            </div>
            <p className="font-serif-luxury italic text-[15px] text-[color:var(--goldenelia-cream)]/85 leading-snug mb-4">
              “{r.text}”
            </p>
            <p className="font-display text-[10px] tracking-[0.25em] uppercase text-[color:var(--goldenelia-gold)]">
              {r.name}
            </p>
            <p className="font-body text-[11px] text-[color:var(--goldenelia-cream)]/50">{r.role}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
);

/* ============================================
   INSTAGRAM FEED (placeholder grid)
   ============================================ */

export const InstagramTeaser = () => (
  <section data-testid="instagram-section" className="relative py-20 px-6">
    <div className="max-w-5xl mx-auto text-center">
      <a
        href="https://www.instagram.com/goldenelia_2026/"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="instagram-link"
        className="inline-flex items-center gap-3 font-display text-[11px] tracking-[0.4em] uppercase text-[color:var(--goldenelia-gold)] hover:text-[color:var(--goldenelia-gold-bright)] transition-colors"
      >
        <Instagram size={18} /> @goldenelia_2026 · Ακολουθήστε στο Instagram
      </a>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-1 mt-8">
        {["/gallery/oil.jpg","/gallery/olives.jpg","/gallery/honey.jpg","/gallery/wine.jpg","/gallery/village.jpg","/gallery/eleourgio.jpg"].map((src, i) => (
          <a
            key={i}
            href="https://www.instagram.com/goldenelia_2026/"
            target="_blank"
            rel="noopener noreferrer"
            className="aspect-square overflow-hidden bg-black group"
          >
            <img src={src} alt="" loading="lazy" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700" />
          </a>
        ))}
      </div>
    </div>
  </section>
);
